import Reflux from 'reflux';
import { OrderedMap, Map, fromJS } from 'immutable';
import BaseMixin from 'flux/stores/BaseMixin';
import IndeterminateLoaderMixin from 'flux/stores/IndeterminateLoaderMixin';
import {
  EditableCellTypes,
  KeyScopes,
} from 'flux/stores/cuesheet/CuesheetConstants';
import CuesheetActions from 'flux/actions/cuesheet/CuesheetActions';
import ShowActions from 'flux/actions/ShowActions';
import CuesheetRowActions from 'flux/actions/cuesheet/CuesheetRowActions';
import CuesheetRowMixin from 'flux/stores/cuesheet/CuesheetRowMixin';
import CuesheetColumnMixin from 'flux/stores/cuesheet/CuesheetColumnMixin';
import CuesheetColumnActions from 'flux/actions/cuesheet/CuesheetColumnActions';
import CuesheetCellActions from 'flux/actions/cuesheet/CuesheetCellActions';
import CuesheetCellMixin from 'flux/stores/cuesheet/CuesheetCellMixin';
import CuesheetRealTimeMixin from 'flux/stores/cuesheet/CuesheetRealTimeMixin';
import CuesheetCursorActions from 'flux/actions/cuesheet/CuesheetCursorActions';
import CuesheetCursorMixin from 'flux/stores/cuesheet/CuesheetCursorMixin';
import CuesheetCoreMixin from 'flux/stores/cuesheet/CuesheetCoreMixin';
import CuesheetDataHelper from 'flux/stores/cuesheet/CuesheetDataHelper';
import RealTimeActions from 'flux/actions/RealTimeActions';
import IntercomActions from 'flux/actions/IntercomActions';
import NewGlobalElementModalActions from 'flux/actions/NewGlobalElementModalActions';
import RouteActions from 'flux/actions/RouteActions';
import PopoverActions from 'flux/actions/PopoverActions';
import CuesheetTrackingActions from 'flux/actions/cuesheet/CuesheetTrackingActions';
import EventViewActions from 'flux/actions/EventViewActions';
import TeamViewActions from 'flux/actions/TeamViewActions';
import UserActions from 'flux/actions/UserActions';
import ModalActions from 'flux/actions/ModalActions';
import UIRegions from 'shared/constants/UIRegions';
import UserSessionStore from 'flux/stores/UserSessionStore';
import CuesheetTrackingStore from 'flux/stores/cuesheet/CuesheetTrackingStore';
import { loadCopyState } from 'flux/stores/copyPersistence';
import preloadItemSetImages from 'shared/flux/helpers/preloadItemSetImages';

/**
 * CuesheetStore
 *
 */
export default Reflux.createStore({
  title: 'CuesheetStore',
  listenables: [
    CuesheetActions,
    CuesheetRowActions,
    CuesheetColumnActions,
    CuesheetCellActions,
    CuesheetCursorActions,
    RealTimeActions,
    RouteActions,
    ShowActions,
  ],

  mixins: [
    BaseMixin,
    IndeterminateLoaderMixin,
    CuesheetRowMixin,
    CuesheetColumnMixin,
    CuesheetCellMixin,
    CuesheetCursorMixin,
    CuesheetRealTimeMixin,
    CuesheetCoreMixin,
  ],

  init() {
    this.setState(this._getInitState());
    this.listenTo(UserSessionStore, 'onUserSession');
    this.listenTo(CuesheetTrackingStore, 'onCuesheetTracking');

    this.listenTo(
      UserActions.updatePreference.completed,
      'onUpdateUserPreferenceCompleted',
    );
    this.listenTo(
      UserActions.loadFromSession.completed,
      'onLoadFromSessionCompleted',
    );

    this.listenTo(
      NewGlobalElementModalActions.createGlobalElementFromShowRow.completed,
      'onCreateGlobalElementFromShowRowCompleted',
    );
    this.listenTo(
      CuesheetTrackingActions.setInitialTrackingPosition,
      'onSetInitialTrackingPosition',
    );
    this.listenTo(
      CuesheetTrackingActions.updateTrackableCue.completed,
      'onUpdateTrackableCueCompleted',
    );
    this.listenTo(PopoverActions.show, 'onShowPopover');

    this.listenTo(EventViewActions.populate.failed, 'onEventPopulateFailed');
    this.listenTo(
      EventViewActions.populate.completed,
      'onEventPopulateCompleted',
    );

    this.listenTo(
      TeamViewActions.populate.completed,
      'onTeamPopulateCompleted',
    );
  },

  _getInitState() {
    const state = this.getState();

    let next = Map({
      keyScope: KeyScopes.Default,
      cellToolbarEnabled: false,
      selectedCells: OrderedMap(),
      corruptedCells: Map(),
      userSettings: Map(),
      collabCells: Map(),
      totalDurationInSeconds: 0,
      totalActualDurationInSeconds: 0,
      loading: true,
    });

    /**
     * Only add pastable information if state exists
     */
    if (state) {
      next = next
        .set('pasteMode', state.get('pasteMode'))
        .set('pastableCells', state.get('pastableCells'))
        .set('cutCells', state.get('cutCells'))
        .set('featuredCellRowIndex', state.get('featuredCellRowIndex'))
        .set('pastableCellParams', state.get('pastableCellParams'))
        .set('pasteCellsMode', state.get('pasteCellsMode'))
        .set('pastableRows', state.get('pastableRows'))
        .set('pastableRowIds', state.get('pastableRowIds'))
        .set('rowSelectionMode', state.get('rowSelectionMode'))
        .set('filterRowsOnLoad', state.get('filterRowsOnLoad'))
        .set('event', state.get('event'));

      if (state.has('userId')) {
        next = next.set('userId', state.get('userId'));
      }
      if (state.has('fullScreenFeaturedCell')) {
        next = next.set(
          'fullScreenFeaturedCell',
          state.get('fullScreenFeaturedCell'),
        );
      }
    }

    return next;
  },

  onSetFullScreenFeaturedCell(rowIndex) {
    const fullScreenFeaturedCell = Map({
      cellSpec: Map({ type: EditableCellTypes.ItemTitle }),
      rowIndex,
    });

    this.setInState('fullScreenFeaturedCell', fullScreenFeaturedCell);
  },

  onUpdateAfterReconnect(teamId, eventId, showId) {
    this._onPopulate(teamId, eventId, showId);
  },

  onUpdateAfterReconnectCompleted(result) {
    this._onPopulateCompleted(result);
  },

  onSetCollabCells(collabState) {
    let next = this.getState();
    const itemSetId = next.get('itemSetId');

    next.get('collabCells').forEach((cell) => {
      const rowIndex = this.findRowPosition(itemSetId, cell.get('rowId'), next);
      next = this.touchRow(rowIndex, next);
    });

    next = next.set('collabCells', Map());
    const showId = next.get('showId');
    const collabItems = collabState[showId];
    if (collabItems) {
      collabItems.forEach((collabItem) => {
        const { collaboration, user } = collabItem;
        if (next.get('userId') === user._id) return;
        const { cellSpec, rowIndex, type, rowId } = collaboration;
        if (cellSpec && (rowIndex || rowIndex === 0)) {
          const collabCellPath = [
            'collabCells',
            this.getSelectedCellKey(rowId, Map(cellSpec)),
          ];
          next = next.setIn(
            collabCellPath,
            fromJS({ rowId, cellSpec, user, type }),
          );
          next = this.touchRow(rowIndex, next);
        }
      });
    }

    this.setStateAndTrigger(next);
  },

  onToggleActualDurations() {
    const state = this.getState();
    const show = state.get('show');
    const displayActualDuration = show.get('displayActualDuration');

    ShowActions.saveField(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      'displayActualDuration',
      !displayActualDuration,
    );
  },

  onSaveFieldCompleted(show) {
    let next = this.getState();
    next = next.set('show', fromJS(show));
    CuesheetRowActions.bustRowHeightCache();
    next = this.touchAllRows(next);
    this.setState(next);
    this.setStateAndTrigger(this.rebuildSelectableCellList());
  },

  onEventPopulateCompleted(event) {
    this.setInStateAndTrigger(
      'event',
      Map({
        timeZone: event.timeZone,
        twentyFourHourClock: event.twentyFourHourClock,
        guestPass: event.guestPass,
        title: event.title,
        photo: event.photo,
      }),
    );
  },

  onEventPopulateFailed(err) {
    /**
     * If event fails to populate with a 404 it is likely a deleted event.
     * Throw modal error for deleted events.
     */
    if (err.status === 404) {
      ModalActions.show(UIRegions.ViewingDeletedEventError);
    }
  },

  onTeamPopulateCompleted(team) {
    const { title, photo } = team;
    this.setInStateAndTrigger(
      'team',
      Map({
        title,
        photo,
      }),
    );
  },

  /**
   * Clear the cuesheet state (used in unmount scenarios)
   */
  onClear() {
    this.setStateAndTrigger(this._getInitState());
  },

  /**
   * We clear the scroll index state when a popover is shown (ref #1290)
   */
  onShowPopover() {
    this.setStateAndTrigger(this.getClearedScrollIndexState());
  },

  onLoadFromSessionCompleted(user) {
    if (!user.preferences) return;

    this.setInState('userSettings', fromJS(user.preferences));
  },

  onUserSession(user) {
    this.setInState('userId', user.get('_id'));
    this._user = user;

    if (this._onUserSessionCallback) {
      this._onUserSessionCallback();
      delete this._onUserSessionCallback;
    }

    //Do this in order to force refresh,
    //since we need to make sure permission changes are reflected in cuesheet
    //A change in the user doesn't trigger a render of the FDT, so we are forcing
    //See https://github.com/SHOFLO/Issues/issues/1513
    setTimeout(() => {
      this.setStateAndTrigger(this.touchAllRows());
    }, 500);
  },

  onCuesheetTracking(trackingState) {
    this._trackingState = trackingState;

    if (this.getInState('rows')) {
      this.setStateAndTrigger(this._setTrackableUsersInRows());
    }
  },

  _setTrackableUsersInRows(state) {
    if (!this._trackingState.has('trackableUsers')) return state;

    const trackableUsers = this._trackingState.get('trackableUsers');

    let next = state || this.getState();

    trackableUsers.forEach((user) => {
      next = this._setTrackedUser(user, next);
    });

    if (this._trackingState.get('selectedTrackableUser')) {
      let trackableUserId = this._trackingState.get('selectedTrackableUser');
      if (trackableUsers.get(trackableUserId)) {
        next = this._setTrackedUser(trackableUsers.get(trackableUserId), next);
      }
    }

    return next;
  },

  _setTrackedUser(user, next) {
    const eventId = this.getInState('eventId');

    const userId = user.get('_id');

    next = this._getClearedTrackedUserName(userId, next);

    if (user.hasIn(['trackableState', eventId])) {
      let trackableState = user.getIn(['trackableState', eventId]);
      if (trackableState.get('showId') !== this.getInState('showId'))
        return next;

      const trackedRowIndex = this.findRowPosition(
        trackableState.get('itemSetId'),
        trackableState.get('itemSetRowId'),
        next,
      );

      if (!next.hasIn(['rows', trackedRowIndex, 'trackedUsers'])) {
        next = next.setIn(['rows', trackedRowIndex, 'trackedUsers'], Map());
      }

      const nameString =
        userId === this._getUser().get('_id')
          ? 'Me'
          : `${user.get('firstName')} ${user.get('lastName').charAt(0)}`;

      /**
       * 2. Add the user first/last to the new row
       */
      next = next.setIn(
        ['rows', trackedRowIndex, 'trackedUsers', userId],
        nameString,
      );
    }

    return next;
  },

  onUpdateUserPreferenceCompleted(user) {
    let userSettings = this.getState().get('userSettings');
    const userPreferences = fromJS(user.preferences);

    userSettings = userSettings
      ? fromJS(Object.assign(userSettings, user.preferences))
      : userPreferences;

    this.setInState('userSettings', userSettings);

    CuesheetRowActions.bustRowHeightCache();
  },

  _getUser() {
    return this._user;
  },

  _getInitTrackableCue() {
    return this._initialTrackableCue;
  },

  clearInitTrackableCue() {
    delete this._initialTrackableCue;
  },

  /**
   * Allows the store to be populated initially w/ a cue to set tracking focus on..
   * this is really used when tracking is set but the tracked location is in another show than what's currently in state
   *
   * @param showId the id of the show for tracking
   * @param initialTrackableItemSetId the initial item set ID
   * @param initialTrackableItemRowId the initial item row ID
   * @param user the user object of the user to track
   */
  onSetInitialTrackingPosition(
    showId,
    initialTrackableItemSetId,
    initialTrackableItemRowId,
    user,
  ) {
    this._initialTrackableCue = {
      showId: showId,
      itemSetId: initialTrackableItemSetId,
      itemSetRowId: initialTrackableItemRowId,
      user,
    };
  },

  onUpdateTrackableCueCompleted(user) {
    if (user.proof) {
      let { trackableState } = user;
      trackableState = trackableState[this.getInState('eventId')];

      const { itemSetId, itemSetRowId } = trackableState;

      const rowIndex = this.findRowPosition(itemSetId, itemSetRowId);

      const state = this.getState();
      const path = state.getIn(['rows', rowIndex, 'path']);
      const nextState = state
        .setIn(['rowMap'].concat(path).concat('proof'), fromJS(user.proof))
        .delete('scrollRowWithOffsetIndex');

      this.setStateAndTrigger(this.touchRow(rowIndex, nextState));

      CuesheetRowActions.bustRowHeightCache(path[2]);
    }
  },

  onClearScrollIndexes() {
    this.setStateAndTrigger(
      this.getState()
        .delete('scrollRowIndex')
        .delete('scrollColumnIndex')
        .delete('scrollRowWithOffsetIndex'),
    );
  },

  onSetCopyStateFromSession() {
    const copyState = loadCopyState();
    this.mergeStateAndTrigger(copyState);
  },

  onPopulate(teamId, eventId, showId) {
    this._onPopulate(teamId, eventId, showId);
  },

  onPopulateCompleted(result) {
    if (!this.getInState('userId')) {
      this._onUserSessionCallback = () => this._onPopulateCompleted(result);
      UserActions.loadFromSession();
    } else {
      this._onPopulateCompleted(result);
    }
  },

  _onPopulate(teamId, eventId, showId) {
    /**
     * Clear state, but keep some values that need to survive/persist
     */
    const next = this._getInitState()
      .set('pasteMode', this.getInState('pasteMode'))
      .set('pastableRows', this.getInState('pastableRows'))
      .set('pastableRowIds', this.getInState('pastableRowIds'))
      .set('rowSelectionMode', this.getInState('rowSelectionMode'))
      .set('userId', this.getInState('userId'))
      .set('event', this.getInState('event'))
      .set('teamId', teamId)
      .set('eventId', eventId)
      .set('showId', showId)
      .set('userSettings', this.getInState('userSettings'));

    RealTimeActions.subscribeToEvent(eventId);
    RealTimeActions.subscribeToShow(showId, { showId, eventId, teamId });
    IntercomActions.updateSettings({ showId });

    this.setStateAndTrigger(next);
  },

  _onPopulateCompleted(result) {
    const { itemSetList, show, globalElementShowItems } = result;

    //NOTE: the order here is important/relevant

    preloadItemSetImages(itemSetList, this.getInState('userId')).then(() => {
      this._populateShowData(show);
      this._populateItemSetData(itemSetList, globalElementShowItems);
      this._populateTrackedRow();
    });
  },

  _populateShowData(show) {
    this.setInState('show', fromJS(show));
  },

  _populateItemSetData(itemSetList, globalElementShowItems) {
    let next = this.getState();

    const userId = next.get('userId');

    next = next.set('itemSetListId', itemSetList._id);
    next = next.set('globalElementShowItems', globalElementShowItems);

    if (itemSetList.itemSets[0])
      next = next.set('itemSetId', itemSetList.itemSets[0]._id);

    const settings = fromJS(
      itemSetList.settings || { itemColumn: { width: 'normal' } },
    );
    const userSettings = itemSetList.userSettings[userId] || {
      noteColumn: { width: 'narrow' },
    };

    const mergedUserSettings = fromJS(
      Object.assign(this.getState().get('userSettings').toJS(), userSettings),
    );

    next = next.set('userSettings', mergedUserSettings);

    //TODO: clean this up...move it to the data helper
    next = next.withMutations((st) => {
      const rowMap = CuesheetDataHelper.createNestedMap(
        itemSetList,
        globalElementShowItems,
      );
      //set the item set list settings (global settings for this list)
      st.set('settings', settings);
      //set the row index data
      st.set('rows', CuesheetDataHelper.createRowIndex(itemSetList, rowMap));
      //set the row map data
      st.set('rowMap', rowMap);

      //set the column data
      const columnOrder = CuesheetDataHelper.getColumnOrder(
        itemSetList,
        next.get('userId'),
      );
      const defaultColumnOrder = columnOrder;
      const fullColumnOrder = CuesheetDataHelper.getFullColumnOrder(
        itemSetList,
        next.get('userId'),
      );
      const columnOrderGuest =
        CuesheetDataHelper.getGuestColumnOrder(itemSetList);

      if (columnOrderGuest.size > fullColumnOrder.size) {
        columnOrderGuest.forEach((guestColumnId) => {
          if (
            fullColumnOrder.findIndex(function (fullColumnId) {
              return fullColumnId === guestColumnId;
            }) < 0
          ) {
            CuesheetColumnActions.deleteColumnGuestPass(guestColumnId);
          }
        });
      }

      this.setColumnState(columnOrder, itemSetList.columns, st);

      st.set('fullColumnOrder', fullColumnOrder);
      st.set(
        'fullColumns',
        CuesheetDataHelper.getSortedColumns(
          itemSetList.columns,
          fullColumnOrder,
        ),
      );
      st.set('defaultColumnOrder', defaultColumnOrder);
      st.set('columnOrderGuest', columnOrderGuest);

      this.setGuestPassColumnState(columnOrderGuest, itemSetList.columns, st);
    });

    next = CuesheetDataHelper.getStateWithDerivedCalculations(
      next,
      next.getIn(['show', 'autoTimeCalculation']),
      next.getIn(['show', 'startOn']),
    );

    next = CuesheetDataHelper.getStateWithTotalDurations(next);

    const filterRowsOnLoad = next.get('filterRowsOnLoad');

    if (filterRowsOnLoad) {
      CuesheetRowActions.filterGlobalElementManagerRows(
        filterRowsOnLoad.field,
        filterRowsOnLoad.values,
      );
    }

    next = next.set('loading', false);
    const fullScreenFeaturedCell = next.get('fullScreenFeaturedCell');
    next = next.delete('fullScreenFeaturedCell');
    this.setStateAndTrigger(this._setTrackableUsersInRows(next));

    if (fullScreenFeaturedCell) {
      this.setCursor(
        fullScreenFeaturedCell.get('rowIndex'),
        fullScreenFeaturedCell.get('cellSpec'),
        false,
      );
    }
  },

  _populateTrackedRow() {
    //1. If the cue has initial tracking focus data, use this first
    const initialTrackableCue = this._getInitTrackableCue();
    if (
      initialTrackableCue &&
      this.getInState('showId') === initialTrackableCue.showId
    ) {
      this.setTrackedRowTransient(
        initialTrackableCue.showId,
        initialTrackableCue.itemSetId,
        initialTrackableCue.itemSetRowId,
        initialTrackableCue.user,
      );

      return;
    }

    //2. Default to user's persistent trackable state
    if (this._getUser().get('trackableState')) {
      const trackableState =
        this._getUser().get('trackableState')[this.getInState('eventId')];
      if (trackableState) {
        this.setTrackedRowTransient(
          trackableState.showId,
          trackableState.itemSetId,
          trackableState.itemSetRowId,
        );
      }
    }
  },

  onPopulateFailed(res) {
    this.setInState('loadFailureCode', res.status);
    this.setLoading(false);
  },

  onDeleteActualDurations(rowIds) {
    const state = this.getState();

    CuesheetActions.deleteActualDurationsPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetId'),
      rowIds,
    );
  },

  onDeleteActualDurationsPersistentCompleted(result) {
    const { rows } = result;
    const itemSetId = result._id;
    this._deleteActualDurations(rows, itemSetId);
  },

  onDeleteActualDurationsRealTime(params, result) {
    const { showId, itemSetId } = params;
    const { rows } = result;
    if (showId !== this.getInState('showId')) return;

    this._deleteActualDurations(rows, itemSetId);
  },

  _deleteActualDurations(rows, itemSetId) {
    const rowsToBust = [];
    let next = this.getState().withMutations((state) => {
      rows.forEach((row) => {
        const path = CuesheetDataHelper.getRowPath(itemSetId, row._id);
        let item = state.getIn(['rowMap'].concat(path));
        if (item && item.has('actualDurationInSeconds')) {
          rowsToBust.push(row._id);
          item = item.delete('actualDurationInSeconds');
          state.setIn(['rowMap'].concat(path), item);
        }
      });
    });
    next = CuesheetDataHelper.getStateWithTotalDurations(next);
    next = this.touchAllRows(next);
    this.setStateAndTrigger(next);
    CuesheetRowActions.bustRowHeightCache(rowsToBust);
  },

  /**
   * Set the column state, which includes the columnOrder (only visible), the column list (only the visible ones) and the
   * list of selectable cells
   *
   */
  setColumnState(columnOrder, columns, state) {
    state = state || this.getState();

    return (
      state
        /**
         * 'columnOrder' is used for views to render columns in the correct order
         */
        .set('columnOrder', columnOrder)
        /**
         * 'rawColumns' is all the columns, including soft-deleted..used for when we need to show a column title, including one that's been deleted
         */
        .set('rawColumns', fromJS(columns))
        /**
         * 'columns' is a sorted columns, and excludes soft-deleted
         */
        .set(
          'columns',
          CuesheetDataHelper.getSortedColumns(columns, columnOrder),
        )
        /**
         * 'selectableCells' is a list of cells that can be selected...used for cursor navigation matrix
         */
        .set('selectableCells', this.getSelectableCellList(columnOrder))
    );
  },

  /**
   * Set Guest Pass Column Order
   *
   * @param columnOrder //Default column order
   * @param columns //List of column objects for show
   * @param state
   *
   */
  setGuestPassColumnState(columnOrder, columns, state) {
    const sortedColumns = CuesheetDataHelper.getSortedColumns(
      columns,
      columnOrder,
    );

    state = state || this.getState();
    return state.set('columnsGuestPass', sortedColumns);
  },
});
